import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Copy, Download, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Reusable TOTP enrollment widget. Works with a normal session (Security page) OR an
// enforced enrollment challenge (forced setup) — auth is carried by httpOnly cookies.
// onDone(res) receives the enroll/verify response ({ enabled, backup_codes, user? }).
export function MfaEnroll({ onDone, onCancel }) {
  const [step, setStep] = useState("intro"); // intro | scan | codes
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null); // { qr_data_uri, manual_key, otpauth_uri }
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [enrollRes, setEnrollRes] = useState(null); // enroll/verify response held for the Done handoff
  const [copied, setCopied] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/start");
      setSetup(data);
      setStep("scan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start setup");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/verify", { code: code.trim() });
      setBackupCodes(data.backup_codes || []);
      setEnrollRes(data);
      setStep("codes");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid code");
    } finally { setBusy(false); }
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(setup?.manual_key || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const blob = new Blob(
      [`DEI TradeMatch AI — MFA backup codes\nGenerated: ${new Date().toISOString()}\n\n${backupCodes.join("\n")}\n\nEach code works once. Store them somewhere safe.`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dei-tradematch-mfa-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (step === "intro") {
    return (
      <div className="space-y-4" data-testid="mfa-enroll-intro">
        <p className="text-sm text-slate-600">
          Add an authenticator app (Google Authenticator, Authy, 1Password…) as a second step
          when you sign in. You'll scan a QR code and enter a 6-digit code.
        </p>
        <div className="flex gap-2">
          <Button onClick={start} disabled={busy} data-testid="mfa-enroll-start-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8]">
            {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <ShieldCheck size={16} className="mr-2" />}
            Set up authenticator
          </Button>
          {onCancel && <Button variant="ghost" onClick={onCancel} data-testid="mfa-enroll-cancel-btn">Cancel</Button>}
        </div>
      </div>
    );
  }

  if (step === "scan") {
    return (
      <form onSubmit={verify} className="space-y-4" data-testid="mfa-enroll-scan">
        <p className="text-sm text-slate-600">Scan this QR code with your authenticator app, then enter the 6-digit code it shows.</p>
        {setup?.qr_data_uri && (
          <img src={setup.qr_data_uri} alt="MFA QR code" width={180} height={180}
            className="rounded-lg border border-slate-200 bg-white p-2" data-testid="mfa-enroll-qr" />
        )}
        <div>
          <p className="text-xs text-slate-500 mb-1">Can't scan? Enter this key manually:</p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-slate-100 rounded px-2 py-1 break-all" data-testid="mfa-enroll-manual-key">{setup?.manual_key}</code>
            <Button type="button" variant="outline" size="sm" onClick={copyKey} data-testid="mfa-enroll-copy-key">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </Button>
          </div>
        </div>
        <input
          inputMode="numeric" pattern="[0-9]*" maxLength={6} required
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          data-testid="mfa-enroll-code-input" placeholder="123456"
          className="w-40 h-11 tracking-[0.4em] text-center text-lg px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] outline-none"
        />
        <div className="flex gap-2">
          <Button type="submit" disabled={busy || code.length !== 6} data-testid="mfa-enroll-verify-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8]">
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Verify & enable"}
          </Button>
          {onCancel && <Button type="button" variant="ghost" onClick={onCancel} data-testid="mfa-enroll-cancel-btn">Cancel</Button>}
        </div>
      </form>
    );
  }

  // codes
  return (
    <div className="space-y-4" data-testid="mfa-enroll-backup-codes">
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
        Save these backup codes now. Each works once if you lose your authenticator. They won't be shown again.
      </div>
      <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-4 font-mono text-sm">
        {backupCodes.map((c) => (<span key={c} data-testid={`mfa-backup-code-${c}`}>{c}</span>))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={download} data-testid="mfa-download-backup-btn"><Download size={16} className="mr-2" /> Download codes</Button>
        <Button onClick={() => onDone?.(enrollRes)} data-testid="mfa-enroll-done-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8]">
          I've saved my codes
        </Button>
      </div>
    </div>
  );
}
